/**
 * Track Filter Parameters
 *
 * A parameter object that consolidates related filter parameters
 * passed together repeatedly across track filtering functions.
 *
 * Benefits:
 * - Reduces the "Long Parameter List" code smell
 * - Provides helper getters for common filter checks
 * - Makes filter state easier to pass and reason about
 */

/**
 * Creates a TrackFilterParams object with computed helper properties.
 *
 * @param {Object} params
 * @param {string} params.accountId - Account ID to filter by
 * @param {string} params.productId - Product ID to filter by
 * @param {string} params.reloadYardCommodityId - Commodity ID to filter by (product fallback)
 * @param {string[]} params.trackIds - Array of track IDs to filter by
 * @param {string} params.searchTerm - Search term for text search
 * @returns {Object} Filter params object with helper getters
 */
export const createFilterParams = ({
  accountId,
  productId,
  reloadYardCommodityId,
  trackIds,
  searchTerm,
} = {}) => ({
  // Core filter values
  accountId,
  productId,
  reloadYardCommodityId,
  trackIds: trackIds || [],
  searchTerm,

  // Helper getters for common checks
  get hasAccountFilter() {
    return !!this.accountId;
  },
  get hasProductFilter() {
    return !!this.productId;
  },
  get hasTrackFilter() {
    return Array.isArray(this.trackIds) && this.trackIds.length > 0;
  },
  get hasSearch() {
    return !!this.searchTerm;
  },
  get hasAnyFilter() {
    return (
      this.hasAccountFilter ||
      this.hasProductFilter ||
      this.hasTrackFilter ||
      this.hasSearch
    );
  },
});

/**
 * Checks if a car matches the given filter params.
 * Reusable matching logic for processTrackData (and any filter params consumers).
 *
 * @param {Object} car - Track car object
 * @param {Object} filterParams - Filter params created by createFilterParams
 * @returns {boolean} True if car matches all filters
 */
export const carMatchesFilters = (car, filterParams) => {
  const accountId = car?.accountId;
  const inbound = car?.inbound;
  const outbound = car?.outbound;
  const inboundInventory = inbound?.inventories?.items?.[0];
  const outboundInventory = outbound?.shipmentLineItems?.items?.[0]?.inventory;
  const productId = inboundInventory?.productId ?? outboundInventory?.productId;
  const inboundCommodity = inbound?.reloadYardCommodityId;
  const outboundCommodity = outbound?.reloadYardCommodityId;

  // Account filter
  if (filterParams.hasAccountFilter && filterParams.accountId !== accountId) {
    return false;
  }

  // Product / commodity filter
  if (filterParams.hasProductFilter) {
    const matchesProductId = filterParams.productId === productId;
    const matchesCommodity =
      filterParams.reloadYardCommodityId &&
      (filterParams.reloadYardCommodityId === inboundCommodity ||
        filterParams.reloadYardCommodityId === outboundCommodity);
    if (!matchesProductId && !matchesCommodity) {
      return false;
    }
  } else if (filterParams.reloadYardCommodityId) {
    const carCommodity = inboundCommodity ?? outboundCommodity ?? null;
    if (carCommodity !== filterParams.reloadYardCommodityId) {
      return false;
    }
  }

  return true;
};
